import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  IApiToastMessageConfig,
  IBidOptionResponse,
} from "@miplatform/types";

export const useBidOptions = () => {
  const [options, setOptions] = useState<IBidOptionResponse[]>([]);
  const [optionDetails, setOptionDetails] =
    useState<IBidOptionResponse | null>(null);

  const stateManager = useComponentState();

  const getOptions = ({
    planId,
    filters,
    sort = "name",
    top,
    skip,
  }: IGetOptionsRequest) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.options.getAll({
        planId,
        filters,
        sort,
        top,
        skip,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptions(response.data);
      }
      return response;
    });
  };

  const getOption = ({
    planId,
    optionId,
    toastMessageConfig,
  }: IGetOptionRequest) => {
    return stateManager.run(async () => {
      const response = await homeService.plans.options.getById({
        planId,
        optionId,
        toastMessageConfig,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptionDetails(response.data);
      }
      return response;
    });
  };

  const createOption = ({
    planId,
    data,
    toastMessageConfig,
  }: IMutateOptionRequest) => {
    return stateManager.run(() =>
      homeService.plans.options.create({ planId, data, toastMessageConfig })
    );
  };

  const updateOption = ({
    planId,
    data,
    toastMessageConfig,
  }: IMutateOptionRequest) => {
    return stateManager.run(() =>
      homeService.plans.options.update({ planId, data, toastMessageConfig })
    );
  };

  const deleteOption = ({
    planId,
    id,
    modifiedOn,
    toastMessageConfig,
  }: IDeleteOptionRequest) => {
    return stateManager.run(() =>
      homeService.plans.options.delete({
        planId,
        id,
        modifiedOn,
        toastMessageConfig,
      })
    );
  };

  return {
    isLoading: stateManager.isBusy(),
    options,
    setOptions,
    optionDetails,
    setOptionDetails,
    getOptions,
    getOption,
    createOption,
    updateOption,
    deleteOption,
  };
};

interface IGetOptionsRequest extends IApiRequest {
  planId: string;
}

interface IGetOptionRequest {
  planId: string;
  optionId: string;
  toastMessageConfig?: IApiToastMessageConfig;
}

interface IMutateOptionRequest {
  planId: string;
  data: Partial<IBidOptionResponse>;
  toastMessageConfig?: IApiToastMessageConfig;
}

interface IDeleteOptionRequest {
  planId: string;
  id: string;
  modifiedOn?: string | null;
  toastMessageConfig?: IApiToastMessageConfig;
}
